import React, { useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { FiEdit2, FiTrash2, FiPlus, FiCheck, FiX, FiInfo } from 'react-icons/fi';
import '../styles/Users.css';

const VERTICAL_LABELS = {
  estacionamento: 'Estacionamento Comercial',
  condominio: 'Condomínio Residencial',
  clube_social: 'Clube Social'
};

const FORM_VAZIO = { username: '', password: '', email: '', role: 'viewer', status: 'ativo' };

const formatarData = (valor) => {
  if (!valor) return null;
  const d = new Date(valor);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const Users = () => {
  const { user: usuarioAtual } = useAuth();
  const [users, setUsers] = useState([]);
  const [catalogo, setCatalogo] = useState(null);   // { vertical, roles: [...] }
  const [falha, setFalha] = useState(null);
  const [perfilAberto, setPerfilAberto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(FORM_VAZIO);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await api.get('/api/users');
      setUsers(response.data);
      setFalha(null);
    } catch (error) {
      // Lista vazia e requisição recusada davam a mesma tela — "Nenhum usuário
      // cadastrado" —, o que mandou procurar o problema no banco quando ele
      // estava na sessão.
      const msg = error.response?.status === 403
        ? 'Sua sessão não alcança a gestão de usuários. Saia e entre de novo para atualizar o seu perfil de acesso.'
        : (error.response?.data?.error || 'Não foi possível carregar os usuários.');
      setFalha(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    // O catálogo diz quantas funcionalidades cada perfil alcança NESTA vertical.
    // Sem ele a tela ofereceria quatro perfis sem dizer o que muda entre eles.
    api.get('/api/users/roles')
      .then(r => setCatalogo(r.data))
      .catch(() => setCatalogo(null));
  }, [fetchUsers]);

  const perfis = catalogo?.roles || [];
  const rotuloPerfil = (role) => perfis.find(p => p.role === role)?.label || role;

  /**
   * Onda 93: quem está olhando alcança um usuário deste perfil?
   *
   * A resposta vem do backend, em `pode_conceder` — a mesma régua que decide a
   * criação decide a edição e a exclusão, e ela mora num lugar só. Enquanto o
   * catálogo não chegou, assume-se que sim: a tela não deve travar por causa de
   * uma requisição lenta, e o backend recusa de qualquer forma.
   */
  const alcanco = (role) => {
    const p = perfis.find(x => x.role === role);
    return !p || p.pode_conceder !== false;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSalvando(true);
    try {
      if (editingId) {
        // Senha em branco significa "manter a atual" — enviá-la vazia faria o
        // backend gravar o hash de uma senha vazia.
        const payload = { ...formData };
        if (!payload.password) delete payload.password;
        await api.put(`/api/users/${editingId}`, payload);
        toast.success('Usuário atualizado');
      } else {
        await api.post('/api/users', formData);
        toast.success('Usuário criado');
      }

      setFormData(FORM_VAZIO);
      setEditingId(null);
      setShowForm(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao salvar usuário');
    } finally {
      setSalvando(false);
    }
  };

  const handleEdit = (user) => {
    setFormData({
      username: user.username || '',
      email: user.email || '',
      password: '',
      role: user.role,
      status: user.status || 'ativo'
    });
    setEditingId(user.id);
    setShowForm(true);
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Excluir o usuário "${user.username}"? A ação não pode ser desfeita.`)) return;
    try {
      await api.delete(`/api/users/${user.id}`);
      toast.success('Usuário excluído');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao excluir usuário');
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData(FORM_VAZIO);
  };

  const abrirNovo = () => {
    setEditingId(null);
    setFormData(FORM_VAZIO);
    setShowForm(true);
  };

  if (loading) {
    return <div className="loading">Carregando usuários...</div>;
  }

  const verticalLabel = VERTICAL_LABELS[catalogo?.vertical] || catalogo?.vertical;

  return (
    <div className="users-page">
      <div className="page-header">
        <div>
          <h1>Usuários</h1>
          {verticalLabel && (
            <p className="page-subtitle">
              Os perfis abaixo valem para a vertical <strong>{verticalLabel}</strong>.
              Cada um alcança só as funcionalidades que existem nela.
            </p>
          )}
        </div>
        <button className="btn btn-primary" onClick={showForm ? handleCancel : abrirNovo}>
          {showForm ? <><FiX /> Fechar</> : <><FiPlus /> Novo usuário</>}
        </button>
      </div>

      {perfis.length > 0 && (
        <div className="roles-legend">
          {perfis.map(p => (
            <button
              type="button"
              key={p.role}
              className={`role-card ${perfilAberto === p.role ? 'open' : ''}`}
              onClick={() => setPerfilAberto(perfilAberto === p.role ? null : p.role)}
              aria-expanded={perfilAberto === p.role}
            >
              <span className={`badge badge-${p.role}`}>{p.label}</span>
              <span className="role-count">{p.feature_count} funcionalidades</span>
              <span className="role-hint"><FiInfo /> ver quais</span>
            </button>
          ))}
        </div>
      )}

      {perfilAberto && (
        <div className="role-detail">
          <h3>{rotuloPerfil(perfilAberto)} — o que alcança</h3>
          <ul>
            {perfis.find(p => p.role === perfilAberto)?.features.map(f => (
              <li key={f.key}>{f.label}</li>
            ))}
          </ul>
        </div>
      )}

      {showForm && (
        <div className="form-container">
          <form onSubmit={handleSubmit}>
            <h2 className="form-title">{editingId ? 'Editar usuário' : 'Novo usuário'}</h2>

            <div className="form-group">
              <label htmlFor="u-username">Usuário</label>
              <input
                id="u-username"
                type="text"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="u-email">Email</label>
              <input
                id="u-email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="u-password">Senha {editingId && <span className="field-hint">(em branco mantém a atual)</span>}</label>
              <input
                id="u-password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required={!editingId}
                autoComplete="new-password"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="u-role">Perfil de acesso</label>
                <select
                  id="u-role"
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  disabled={editingId === usuarioAtual?.id}
                >
                  {/* Onda 93: o seletor só oferece o que quem está olhando pode
                      de fato conceder. O backend manda `pode_conceder` por
                      perfil; sem filtrar aqui, a tela ofereceria
                      "Superadministrador" a um administrador, que escolheria,
                      salvaria e levaria um 403 — erro que o sistema poderia ter
                      evitado em vez de cobrar.

                      A lista de reserva (quando o catálogo ainda não chegou)
                      para no administrador de propósito: oferecer menos do que
                      se pode é um aborrecimento, oferecer mais é uma recusa. */}
                  {(perfis.length > 0
                    ? perfis.filter(p => p.pode_conceder !== false)
                    : [{ role: 'viewer', label: 'Consulta' }, { role: 'operator', label: 'Operador' },
                       { role: 'supervisor', label: 'Supervisão' }, { role: 'admin', label: 'Administrador' }]
                  ).map(p => (
                    <option key={p.role} value={p.role}>{p.label}</option>
                  ))}
                </select>
                {editingId === usuarioAtual?.id && (
                  <span className="field-hint">Você não pode alterar o próprio perfil.</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="u-status">Situação</label>
                <select
                  id="u-status"
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  disabled={editingId === usuarioAtual?.id}
                >
                  <option value="ativo">Ativo</option>
                  <option value="inativo">Inativo</option>
                </select>
                <span className="field-hint">Uma conta inativa não entra na plataforma.</span>
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-success" disabled={salvando}>
                <FiCheck /> {salvando ? 'Salvando...' : 'Salvar'}
              </button>
              <button type="button" className="btn btn-danger" onClick={handleCancel}>
                <FiX /> Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="users-table">
        {falha ? (
          <p className="no-data error">{falha}</p>
        ) : users.length === 0 ? (
          <p className="no-data">Nenhum usuário cadastrado</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Usuário</th>
                <th>Email</th>
                <th>Perfil</th>
                <th>Situação</th>
                <th>Último acesso</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => {
                const ehEuMesmo = user.id === usuarioAtual?.id;
                return (
                  <tr key={user.id} className={user.status === 'inativo' ? 'row-inactive' : ''}>
                    <td>
                      {user.username}
                      {ehEuMesmo && <span className="tag-self">você</span>}
                    </td>
                    <td>{user.email}</td>
                    <td><span className={`badge badge-${user.role}`}>{rotuloPerfil(user.role)}</span></td>
                    <td>
                      <span className={`status-dot status-${user.status}`} />
                      {user.status === 'inativo' ? 'Inativo' : 'Ativo'}
                    </td>
                    <td className="cell-date">{formatarData(user.last_login_at) || <span className="never">nunca entrou</span>}</td>
                    <td className="cell-actions">
                      {/* Onda 93: quem não alcança o perfil da linha não recebe
                          os botões. O backend recusa de qualquer forma — esta é
                          a camada que evita o clique inútil, não a que protege.
                          Segurança que depende da tela não é segurança. */}
                      <button
                        className="btn btn-sm btn-info"
                        onClick={() => handleEdit(user)}
                        disabled={!alcanco(user.role)}
                        title={!alcanco(user.role)
                          ? `Seu perfil não alcança um ${rotuloPerfil(user.role)}` : undefined}
                      >
                        <FiEdit2 /> Editar
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDelete(user)}
                        disabled={ehEuMesmo || user.id === 1 || !alcanco(user.role)}
                        title={user.id === 1 ? 'O superadministrador original não pode ser excluído'
                          : ehEuMesmo ? 'Você não pode excluir a própria conta'
                          : !alcanco(user.role) ? `Seu perfil não alcança um ${rotuloPerfil(user.role)}`
                          : undefined}
                      >
                        <FiTrash2 /> Excluir
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default Users;
